import { TextField, TextFieldProps, useTheme, alpha } from "@mui/material";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../configureStore";

interface FormFieldProps extends Omit<TextFieldProps, 'value' | 'onChange' | 'sx'> {
    fieldPath: string;
    value: any;
    handleFieldChange: (fieldPath: string, value: any) => void;
    lowConfidence?: boolean;
    isImportant?: boolean;
}

const FormField = ({
    fieldPath,
    value,
    handleFieldChange,
    lowConfidence = false,
    isImportant = false,
    ...textFieldProps
}: FormFieldProps) => {
    const theme = useTheme();
    const showOnlyImportant = useSelector<RootState, boolean>(({ ui }) => ui.showOnlyImportantFields);

    const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        handleFieldChange(fieldPath, event.target.value);
    }, [fieldPath, handleFieldChange]);

    // If showOnlyImportant is true and this field is not important, don't render it
    if (showOnlyImportant && !isImportant) {
        return null;
    }

    const importantBorderColor = theme.palette.mode === 'dark' ? '#ffb74d' : '#f57c00';

    const textFieldStyles = {
        '& .MuiInputBase-root': {
            backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.8)
                : theme.palette.background.paper,
        },
        '& .MuiInputLabel-root': {
            color: theme.palette.text.secondary,
            ...(isImportant && {
                color: importantBorderColor,
                fontWeight: 600,
            }),
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: isImportant ? importantBorderColor : theme.palette.divider,
                borderWidth: isImportant ? 2 : 1,
            },
            '&:hover fieldset': {
                borderColor: isImportant ? importantBorderColor : theme.palette.primary.main,
            },
            '&.Mui-focused fieldset': {
                borderColor: isImportant ? importantBorderColor : theme.palette.primary.main,
                boxShadow: isImportant ? `0 0 0 3px ${alpha(importantBorderColor, 0.2)}` : 'none',
            },
        },
    };

    return (
        <TextField
            value={value}
            onChange={handleChange}
            size="small"
            fullWidth
            sx={textFieldStyles}
            error={lowConfidence}
            helperText={lowConfidence ? 'Low confidence' : ''}
            {...textFieldProps}
        />
    );
};

export default FormField;
